import React, { useCallback, useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, GitCommit, Rocket, CheckCircle2, Clock, Circle } from "lucide-react";
import { toast } from "sonner";

const PROJECT_STATUSES = [
  { value: "discovery",   label: "Discovery",   color: "#94A3B8" },
  { value: "design",      label: "Design",      color: "#8B5CF6" },
  { value: "development", label: "Development", color: "#0EA5FF" },
  { value: "qa",          label: "QA",          color: "#F59E0B" },
  { value: "uat",         label: "UAT",         color: "#14346E" },
  { value: "deployed",    label: "Deployed",    color: "#5FBF3A" },
  { value: "on_hold",     label: "On hold",     color: "#94A3B8" },
];
const MS_STATUS = { open: Circle, in_progress: Clock, done: CheckCircle2 };
const MS_COLOR = { open: "#94A3B8", in_progress: "#F59E0B", done: "#5FBF3A" };

export default function Digital() {
  const [projects, setProjects] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const load = async () => setProjects((await api.get("/digital/projects")).data);
  useEffect(() => { load(); }, []);

  return (
    <AppLayout title="Digital Projects">
      <div className="flex items-center mb-5">
        <div className="miro-overline">Ship pipeline</div>
        <div className="ml-auto">
          <NewProjectDialog open={newOpen} setOpen={setNewOpen} onCreated={load}/>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {projects.length === 0 && (
          <div className="col-span-full text-center py-16 text-[var(--miro-text-muted)]">No projects yet.</div>
        )}
        {projects.map(p => {
          const st = PROJECT_STATUSES.find(s => s.value === p.status);
          const total = p.milestones?.length || 0;
          const done = p.milestones?.filter(m => m.status === "done").length || 0;
          const pct = total ? Math.round((done / total) * 100) : 0;
          return (
            <div key={p.id} data-testid={`proj-card-${p.id}`} className="miro-card p-5 cursor-pointer" onClick={() => setOpenId(p.id)}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="miro-overline">{st?.label}</div>
                  <h3 className="font-display font-bold text-lg text-[var(--miro-navy)] leading-tight mt-1">{p.title}</h3>
                </div>
                <span className="text-[10px] text-white font-semibold uppercase tracking-wider px-2 py-1 rounded" style={{ background: st?.color }}>{st?.label}</span>
              </div>
              <div className="mt-4">
                <div className="flex justify-between text-[11px] text-[var(--miro-text-muted)] mb-1">
                  <span>Milestones</span><span>{done}/{total}</span>
                </div>
                <div className="h-1.5 rounded-full bg-[var(--miro-bg)] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#0EA5FF,#5FBF3A)" }}/>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-[var(--miro-border)] flex items-center justify-between text-sm">
                <div className="text-xs text-[var(--miro-text-muted)]"><GitCommit size={12} className="inline mr-1"/>{p.change_requests?.length || 0} CRs</div>
                <div className="font-mono font-semibold text-[var(--miro-navy)]">₹{(p.budget || 0).toLocaleString("en-IN")}</div>
              </div>
            </div>
          );
        })}
      </div>

      <ProjectDrawer projectId={openId} open={!!openId} onClose={() => setOpenId(null)} onChanged={load}/>
    </AppLayout>
  );
}

function ProjectDrawer({ projectId, open, onClose, onChanged }) {
  const [proj, setProj] = useState(null);
  const [msTitle, setMsTitle] = useState("");
  const [crTitle, setCrTitle] = useState("");

  const load = useCallback(async () => {
    if (!projectId) return;
    setProj((await api.get(`/digital/projects/${projectId}`)).data);
  }, [projectId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const save = async (patch) => {
    const r = await api.patch(`/digital/projects/${projectId}`, patch);
    setProj(r.data); onChanged?.();
  };

  const addMs = async () => {
    if (!msTitle.trim()) return;
    const r = await api.post(`/digital/projects/${projectId}/milestones`, { title: msTitle, status: "open" });
    setProj(r.data); setMsTitle(""); onChanged?.();
  };

  const cycleMs = async (m) => {
    const next = m.status === "open" ? "in_progress" : m.status === "in_progress" ? "done" : "open";
    const r = await api.patch(`/digital/projects/${projectId}/milestones/${m.id}`, { title: m.title, due_date: m.due_date, status: next });
    setProj(r.data); onChanged?.();
  };

  const delMs = async (id) => {
    const r = await api.delete(`/digital/projects/${projectId}/milestones/${id}`);
    setProj(r.data); onChanged?.();
  };

  const addCr = async () => {
    if (!crTitle.trim()) return;
    const r = await api.post(`/digital/projects/${projectId}/change-requests`, { title: crTitle, status: "open", effort_days: 1, cost: 0 });
    setProj(r.data); setCrTitle(""); onChanged?.();
  };

  const updateCr = async (cr, patch) => {
    const r = await api.patch(`/digital/projects/${projectId}/change-requests/${cr.id}`, {
      title: cr.title, description: cr.description, status: patch.status ?? cr.status,
      effort_days: patch.effort_days ?? cr.effort_days, cost: patch.cost ?? cr.cost,
    });
    setProj(r.data); onChanged?.();
  };

  const remove = async () => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/digital/projects/${projectId}`);
    onChanged?.(); onClose();
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent data-testid="proj-drawer" className="w-full sm:max-w-[600px] p-0 bg-white overflow-y-auto">
        <SheetTitle className="sr-only">Project detail</SheetTitle>
        {proj && (
          <div>
            <div className="px-6 pt-6 pb-4 border-b border-[var(--miro-border)]">
              <div className="miro-overline">Digital project</div>
              <SheetHeader className="mt-1">
                <SheetTitle className="font-display font-black text-2xl text-[var(--miro-navy)] tracking-tight">{proj.title}</SheetTitle>
              </SheetHeader>
              <div className="mt-3 flex items-center gap-2">
                <Select value={proj.status} onValueChange={(v) => save({ status: v })}>
                  <SelectTrigger data-testid="proj-status-select" className="w-40 h-8 text-xs"><SelectValue/></SelectTrigger>
                  <SelectContent>{PROJECT_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
                <button data-testid="proj-delete-btn" onClick={remove} className="ml-auto text-xs text-red-600 hover:text-red-800 flex items-center gap-1"><Trash2 size={13}/> Delete</button>
              </div>
            </div>
            <div className="px-6 py-4 space-y-5">
              <div>
                <Label>Budget (₹)</Label>
                <Input data-testid="proj-budget-input" type="number" defaultValue={proj.budget}
                       onBlur={(e) => save({ budget: Number(e.target.value) || 0 })} className="mt-1"/>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea data-testid="proj-notes" rows={3} defaultValue={proj.notes || ""}
                          onBlur={(e) => save({ notes: e.target.value })} className="mt-1"/>
              </div>

              <div>
                <div className="miro-overline flex items-center gap-1"><Rocket size={11}/> Milestones</div>
                <div className="mt-2 rounded-lg border border-[var(--miro-border)] divide-y divide-[var(--miro-border)]">
                  {(proj.milestones || []).length === 0 && (
                    <div className="px-3 py-4 text-sm text-[var(--miro-text-muted)]">No milestones yet.</div>
                  )}
                  {(proj.milestones || []).map(m => {
                    const Icon = MS_STATUS[m.status] || Circle;
                    return (
                      <div key={m.id} data-testid={`proj-ms-${m.id}`} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                        <button data-testid={`proj-ms-toggle-${m.id}`} onClick={() => cycleMs(m)}>
                          <Icon size={17} style={{ color: MS_COLOR[m.status] }}/>
                        </button>
                        <span className={`flex-1 ${m.status === "done" ? "line-through text-[var(--miro-text-muted)]" : "text-[var(--miro-navy)] font-medium"}`}>{m.title}</span>
                        <button data-testid={`proj-ms-delete-${m.id}`} onClick={() => delMs(m.id)}
                                className="text-[var(--miro-text-muted)] hover:text-red-600">
                          <Trash2 size={13}/>
                        </button>
                      </div>
                    );
                  })}
                  <div className="flex gap-2 p-2">
                    <Input data-testid="proj-ms-title" placeholder="New milestone title…" value={msTitle}
                           onChange={(e) => setMsTitle(e.target.value)}
                           onKeyDown={(e) => e.key === "Enter" && addMs()} className="h-9"/>
                    <Button data-testid="proj-ms-add-btn" onClick={addMs} disabled={!msTitle.trim()} size="sm" style={{ background: "var(--miro-navy)" }}>
                      Add
                    </Button>
                  </div>
                </div>
              </div>

              <div>
                <div className="miro-overline flex items-center gap-1"><GitCommit size={11}/> Change requests</div>
                <div className="mt-2 space-y-2">
                  {(proj.change_requests || []).length === 0 && (
                    <div className="text-sm text-[var(--miro-text-muted)] px-1">No change requests yet.</div>
                  )}
                  {(proj.change_requests || []).map(cr => (
                    <div key={cr.id} data-testid={`proj-cr-${cr.id}`} className="rounded-lg border border-[var(--miro-border)] p-3">
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-sm text-[var(--miro-navy)] flex-1">{cr.title}</div>
                        <Select value={cr.status} onValueChange={(v) => updateCr(cr, { status: v })}>
                          <SelectTrigger data-testid={`proj-cr-status-${cr.id}`} className="h-7 w-28 text-xs"><SelectValue/></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-[var(--miro-text-secondary)]">
                        <div>Effort: <span className="font-mono text-[var(--miro-navy)]">{cr.effort_days}d</span></div>
                        <div>Cost: <span className="font-mono text-[var(--miro-navy)]">₹{(cr.cost || 0).toLocaleString("en-IN")}</span></div>
                      </div>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Input data-testid="proj-cr-title" placeholder="New change request…" value={crTitle}
                           onChange={(e) => setCrTitle(e.target.value)}
                           onKeyDown={(e) => e.key === "Enter" && addCr()} className="h-9"/>
                    <Button data-testid="proj-cr-add-btn" onClick={addCr} disabled={!crTitle.trim()} size="sm" style={{ background: "var(--miro-navy)" }}>
                      Add
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function NewProjectDialog({ open, setOpen, onCreated }) {
  const [f, setF] = useState({ title: "", status: "discovery", budget: 0, notes: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/digital/projects", { ...f, budget: Number(f.budget) || 0 });
      toast.success("Project created");
      setOpen(false);
      setF({ title: "", status: "discovery", budget: 0, notes: "" });
      onCreated?.();
    } catch (e) { toast.error("Create failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-proj-btn" style={{ background: "var(--miro-navy)" }}><Plus size={16} className="mr-1"/> New project</Button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[var(--miro-navy)]">New digital project</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title *</Label><Input data-testid="new-proj-title" value={f.title} onChange={(e) => setF({...f, title: e.target.value})} className="mt-1"/></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Status</Label>
              <Select value={f.status} onValueChange={v => setF({...f, status: v})}>
                <SelectTrigger data-testid="new-proj-status" className="mt-1"><SelectValue/></SelectTrigger>
                <SelectContent>{PROJECT_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Budget (₹)</Label><Input data-testid="new-proj-budget" type="number" value={f.budget} onChange={(e) => setF({...f, budget: e.target.value})} className="mt-1"/></div>
          </div>
          <div><Label>Notes</Label><Textarea data-testid="new-proj-notes" rows={3} value={f.notes} onChange={(e) => setF({...f, notes: e.target.value})} className="mt-1"/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="new-proj-submit" disabled={!f.title.trim() || saving} onClick={submit} style={{ background: "var(--miro-navy)" }}>
            {saving ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
